import {
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ViewType } from '@this+that/models';
import log from 'loglevel';

import { useFetchIndividualUserCommunications } from './fetch/useFetchIndividualUserCommunications';
import { useInboxFetchGroups } from './fetch/useInboxFetchGroups';
import { EmailListFooterStatus } from '../../enums/emailListFooterStatus';
import { FetchResult } from '../../enums/fetchResult';
import { useIntegrationsStore, useUserCommunicationsStore } from '../../store';
import { INBOX_FEATURES_LIMIT } from '../../utils/constants';
import { useInboxAccountFilter } from '../useInboxAccountFilter';
import { useIntersectionSentinel } from '../useIntersectionSentinel';

export const EMAIL_LIST_MIN_LOADING_MS = 500;

export interface EmailListFooterState {
  status: EmailListFooterStatus;
  sentinelRef?: RefCallback<HTMLDivElement>;
  retry?: () => void;
}

interface UseEmailListInfiniteScrollProps {
  viewType: ViewType | null;
}

type LocalStatus = EmailListFooterStatus.LOADING | EmailListFooterStatus.FAILED;

interface LocalState {
  view: ViewType;
  status: LocalStatus | null;
}

export function useEmailListInfiniteScroll({
  viewType,
}: UseEmailListInfiniteScrollProps): Required<EmailListFooterState> {
  const { integrationAccounts } = useIntegrationsStore();
  const { fetchEmailFilters } = useInboxAccountFilter(integrationAccounts);
  const { combinedViewTypes } = useInboxFetchGroups();
  const { handleFetchIndividualUserCommunications, getHasMore } =
    useFetchIndividualUserCommunications({ limit: INBOX_FEATURES_LIMIT });
  const syncedViewTypes = useUserCommunicationsStore(
    (state) => state.syncedViewTypes,
  );

  const [local, setLocal] = useState<LocalState | null>(null);
  const inFlightViewsRef = useRef<Set<ViewType>>(new Set());
  const pendingSettleRef = useRef<{
    timer: ReturnType<typeof setTimeout>;
    apply: () => void;
  } | null>(null);
  const isUnmountedRef = useRef(false);

  useEffect(
    () => () => {
      isUnmountedRef.current = true;
    },
    [],
  );

  useEffect(
    () => () => {
      const pending = pendingSettleRef.current;
      if (!pending) return;
      clearTimeout(pending.timer);
      pendingSettleRef.current = null;
      if (!isUnmountedRef.current) pending.apply();
    },
    [viewType],
  );

  const localStatus = local && local.view === viewType ? local.status : null;
  const isSynced = viewType !== null && syncedViewTypes.includes(viewType);
  const hasMore = viewType !== null && getHasMore(viewType);

  const resolveStatus = (): EmailListFooterStatus => {
    if (!isSynced) return EmailListFooterStatus.AWAITING;
    if (localStatus) return localStatus;
    return hasMore
      ? EmailListFooterStatus.IDLE
      : EmailListFooterStatus.EXHAUSTED;
  };
  const status = resolveStatus();

  const fetchNextBatch = useCallback(async () => {
    if (viewType === null || inFlightViewsRef.current.has(viewType)) return;
    inFlightViewsRef.current.add(viewType);
    const startedAt = Date.now();
    setLocal({ view: viewType, status: EmailListFooterStatus.LOADING });

    const fetchTarget = combinedViewTypes.includes(viewType)
      ? combinedViewTypes
      : viewType;

    let result: FetchResult = FetchResult.FAILED;
    try {
      result = await handleFetchIndividualUserCommunications(
        fetchTarget,
        true,
        fetchEmailFilters,
      );
    } catch (error) {
      log.error('Error fetching more communications:', error);
    }

    inFlightViewsRef.current.delete(viewType);
    const settled: LocalState = {
      view: viewType,
      status:
        result === FetchResult.FAILED ? EmailListFooterStatus.FAILED : null,
    };
    const apply = () => {
      pendingSettleRef.current = null;
      setLocal((current) =>
        current && current.view !== viewType ? current : settled,
      );
    };
    const remaining = EMAIL_LIST_MIN_LOADING_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      apply();
      return;
    }
    pendingSettleRef.current = {
      timer: setTimeout(apply, remaining),
      apply,
    };
  }, [
    viewType,
    combinedViewTypes,
    handleFetchIndividualUserCommunications,
    fetchEmailFilters,
  ]);

  const loadMore = useCallback(() => {
    if (status === EmailListFooterStatus.IDLE) {
      void fetchNextBatch();
    }
  }, [status, fetchNextBatch]);

  const retry = useCallback(() => {
    if (status === EmailListFooterStatus.FAILED) {
      void fetchNextBatch();
    }
  }, [status, fetchNextBatch]);

  const { sentinelRef } = useIntersectionSentinel({
    enabled: status === EmailListFooterStatus.IDLE,
    onIntersect: loadMore,
  });

  return { status, sentinelRef, retry };
}
